// dependencies
import { useMutation } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";

// components
import Modal from "../../../../../../components/Modal";
import ModalContent from "./ModalContent";

// store
import useReportStore from "../../store";

// hooks
import { ADD_QUESTION_GROUPINGS, EDIT_QUESTION_GROUPINGS } from "../../../../hooks/useQuery";

/**
 * Modal that allows you to:
 * 1. select unique questions to be grouped
 * 2. name the question group
 *
 * State of question grouping data is managed using useFetchQuestionData from the parent component
 * for create: see handleCreateGroup
 * for edit: see handleEditGroup
 */
const GroupQuestionModal = () => {
	const queryClient = useQueryClient();

	const [
		questionGroupingSessionID,
		setActiveModal,
		fetchAvailableColumns,
		groupName,
		groupID,
		selectedQuestions,
		clearModal,
		setQuestionGroupModalError,
		getAppliedFilters,
		toggleGridColumn,
		report,
	] = useReportStore((state) => [
		state.report?.questionGroupingSessionID,
		state.setActiveModal,
		state.fetchAvailableColumns,
		state.questionGroupingModal.name,
		state.questionGroupingModal.id,
		state.questionGroupingModal.selectedQuestions,
		state.questionGroupingModal.clearModal,
		state.questionGroupingModal.setQuestionGroupModalError,
		state.getAppliedFilters,
		state.toggleGridColumn,
		state.report,
	]);

	// GraphQL
	const [addQuestionGroupings] = useMutation(ADD_QUESTION_GROUPINGS);
	const [editQuestionGroupings] = useMutation(EDIT_QUESTION_GROUPINGS);

	const handleCreateGroup = async () => {
		if (!groupName) {
			setQuestionGroupModalError("Please enter a group name.");
			return;
		}

		// Add the question grouping, display error modal if we get an error back.
		try {
			const addQuestionGroupingsResult = await addQuestionGroupings({
				variables: {
					questionGroupingSessionID: questionGroupingSessionID,
					newGroups: [
						{
							name: groupName,
							questions: selectedQuestions,
						},
					],
					filters: getAppliedFilters(),
					reportType: report?.type || "reviewReport",
				},
			});

			const questionGroupID = addQuestionGroupingsResult.data.addQuestionGroupings[0];
			// Since the new grouping won't exist in the grid data yet, we need to refetch it.
			queryClient.invalidateQueries({ queryKey: ["getReportGridData"] });
			await fetchAvailableColumns();
			toggleGridColumn(`groupedquestion_${questionGroupID}`);
		} catch (error) {
			if (error instanceof Error && error.toString() === "ApolloError: No questions selected.") {
				setQuestionGroupModalError("No questions selected. Please select at least one question.");
			} else if (
				error instanceof Error &&
				error.toString() === "ApolloError: Question group name already in use."
			) {
				setQuestionGroupModalError("That question group name is already in use. Please use a different name.");
			} else {
				setQuestionGroupModalError(
					"An error has occurred. Please contact Technical Support if the error persists."
				);
				Sentry.captureException(error);
			}
			return;
		}
		clearModal();
		setActiveModal(null);
	};

	const handleEditGroup = async () => {
		if (!groupName) {
			setQuestionGroupModalError("Please enter a group name.");
			return;
		}

		// Update the question grouping, display error modal if we get an error back.
		try {
			await editQuestionGroupings({
				variables: {
					questionGroupingSessionID: questionGroupingSessionID,
					groupingUpdates: [
						{
							groupID: groupID,
							updateObject: {
								questions: selectedQuestions,
								name: groupName,
							},
						},
					],
					filters: getAppliedFilters(),
					reportType: report.type,
				},
			});

			// Since the new grouping won't exist in the grid data yet, we need to refetch it.
			queryClient.invalidateQueries({ queryKey: ["getReportGridData"] });
			await fetchAvailableColumns();
		} catch (error) {
			if (error instanceof Error && error.toString() === "ApolloError: No questions selected.") {
				setQuestionGroupModalError("No questions selected. Please select at least one question.");
			} else if (
				error instanceof Error &&
				error.toString() === "ApolloError: Question group name already in use."
			) {
				setQuestionGroupModalError("That question group name is already in use. Please use a different name.");
			} else {
				setQuestionGroupModalError(
					"An error has occurred. Please contact Technical Support if the error persists."
				);
				Sentry.captureException(error);
			}
			return;
		}
		clearModal();
		setActiveModal(null);
	};

	const handleClose = () => {
		clearModal();
		setActiveModal(null);
	};

	const content = <ModalContent handleCreateGroup={handleCreateGroup} handleEditGroup={handleEditGroup} />;

	return <Modal handleClose={handleClose} size="extralarge" content={content} />;
};

export default GroupQuestionModal;
